import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { strictInject } from '@applied/utils/strictInject';
import { observer } from 'mobx-react';

import './page-snackbar.scss';

import ActionButton from '../ActionButton/ActionButton';
import IconButton from '../IconButton/IconButton';
import DragonfruitSnackbar from '../Snackbar/Snackbar';

import type SnackbarStore from './page_snackbar';

const ANIMATION_DURATION_MS = 250;

const computeStyle = (hidden: boolean): React.CSSProperties | undefined => {
  if (hidden) {
    return undefined;
  } else {
    return { transition: `bottom ${ANIMATION_DURATION_MS}ms` };
  }
};

interface DragonFruitSnackbarProps {
  snackbar: SnackbarStore;
}

export const SnackbarDisplay = strictInject<DragonFruitSnackbarProps>('snackbar')(
  observer((props: DragonFruitSnackbarProps) => {
    const {
      snackbar: { message, actionText, actionHandler, persistent },
      snackbar,
    } = props;

    const [lastMessage, setLastMessage] = useState<string | null>(null);
    const [hidden, setHidden] = useState<boolean>(false);
    const timeoutHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      clearTimeout(timeoutHandle.current! /* Fixme: http://tiny.cc/r4g9vz */);

      if (message !== null && message !== lastMessage) {
        if (lastMessage !== null) {
          setHidden(true);
          timeoutHandle.current = setTimeout(() => {
            setHidden(false);
            setLastMessage(message);
          });
        } else {
          setLastMessage(message);
        }
      }
    }, [message, lastMessage]);

    const wrappedActionHandler = useCallback(() => {
      actionHandler();
      snackbar.hide();
    }, [actionHandler, snackbar]);

    return (
      <CSSTransition
        in={message !== null && !hidden}
        timeout={hidden ? 0 : ANIMATION_DURATION_MS}
        classNames="adp-snackbar"
        unmountOnExit
        mountOnEnter
      >
        <div className="adp-snackbar" style={computeStyle(hidden)} data-testid="adp-snackbar">
          <DragonfruitSnackbar
            text={lastMessage! /* Fixme: http://tiny.cc/r4g9vz */}
            leftExtra={
              persistent ? (
                <IconButton
                  appearance="invisible"
                  icon="CLOSE"
                  size="small"
                  onClick={(): void => snackbar.hide()}
                  ariaLabel="close snackbar"
                />
              ) : undefined
            }
            rightExtras={
              actionText ? (
                <ActionButton appearance="primary" onClick={wrappedActionHandler} size="small">
                  {actionText}
                </ActionButton>
              ) : undefined
            }
            wrapOnOverflow
          />
        </div>
      </CSSTransition>
    );
  }),
);
